import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'reactstrap';
import { Mutex } from 'async-mutex';
import history from '../history';
import api from '../api';
import LicenseChecker from './LicenseChecker';
import NewModal from './NewModal';
import LabeledInput from './LabeledInput';
import UserSelect from './UserSelect';
import { MiniLoader } from './Loader';
import Validator, { GUI_EFFECT_FULL } from './Validator';
import { formatPasteEmailList } from '../utils/email';
import useToast from '../utils/HookToast';
import useError from '../utils/HookErrorUtil';
import { childrenPropTypes } from '../utils/generic-prop-types';
import { teamMembersActions, teamsActions } from '../redux/actions';
import Help from './Help';
import { NewBadge } from './Badges';


function TeamCreateModal(props) {
  const {
    children, admin, i18n, institutionalTeamsMode, organizationIds,
  } = props;
  const { t } = useTranslation();
  const { success, error: showError } = useToast();
  const { handleCatched } = useError();

  const initialState = {
    name: '',
    leaderEmail: '',
    leaderCanCreateProject: false,
    leaderCanInviteMembers: false,
    leaderCanManagePermissions: false,
  };

  const [name, setName] = useState(initialState.name);
  const [leaderEmail, setLeaderEmail] = useState(initialState.leaderEmail);
  // eslint-disable-next-line max-len
  const [leaderCanCreateProject, setLeaderCanCreateProject] = useState(initialState.leaderCanCreateProject);
  // eslint-disable-next-line max-len
  const [leaderCanInviteMembers, setLeaderCanInviteMembers] = useState(initialState.leaderCanInviteMembers);
  // eslint-disable-next-line max-len
  const [leaderCanManagePermissions, setLeaderCanManagePermissions] = useState(initialState.leaderCanManagePermissions);


  const [newOwnerId, setNewOwnerId] = useState(null);
  // For institutional teams mode: organizations available for team creation
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);

  const emailRef = useRef();
  const nameRef = useRef();
  const ownerRef = useRef();
  const mutex = new Mutex();
  const modal = useRef();

  const user = useSelector((state) => state.auth.authUser);

  const canBeTeamLeader = admin || user.limitations.can_be_team_leader;

  const dispatch = useDispatch();

  const addTeam = async (data) => dispatch(teamsActions.create(data, { admin }));
  const addTeamMember = async (data) => dispatch(teamMembersActions.create(data, { admin }));

  // Fetch organization details when in institutional teams mode
  useEffect(() => {
    const fetchOrganizations = async () => {
      if (!institutionalTeamsMode || !organizationIds || organizationIds.length === 0) {
        return;
      }

      setLoadingOrganizations(true);
      try {
        // Fetch details for each organization
        const orgPromises = organizationIds.map((orgId) => api.read('users', orgId));
        const orgResults = await Promise.all(orgPromises);

        const orgs = orgResults.map((org) => ({
          id: org.id,
          label: org.institution || org.label || `Organization ${org.id}`,
        }));

        setOrganizations(orgs);

        // Auto-select first organization if only one is available
        if (orgs.length === 1) {
          setSelectedOrganization(orgs[0].id);
        }
      } catch (error) {
        // Silently fail - organizations will remain empty
        setOrganizations([]);
      } finally {
        setLoadingOrganizations(false);
      }
    };

    fetchOrganizations();
  }, [institutionalTeamsMode, organizationIds]);

  const reset = () => {
    setName(initialState.name);
    setLeaderEmail(initialState.leaderEmail);
    setLeaderCanCreateProject(initialState.leaderCanCreateProject);
    setLeaderCanInviteMembers(initialState.leaderCanInviteMembers);
    setLeaderCanManagePermissions(initialState.leaderCanManagePermissions);
    // Reset organization selection (keep first selected if only one org)
    if (organizations.length > 1) {
      setSelectedOrganization(null);
    }
  };

  const createTeam = async () => {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();

      // Determine owner based on mode
      let ownerId;
      if (admin) {
        ownerId = newOwnerId;
      } else if (institutionalTeamsMode && selectedOrganization) {
        ownerId = selectedOrganization;
      } else {
        ownerId = user.id;
      }

      const params = {
        owner: ownerId,
        name,
      };
      try {
        const team = await addTeam(params);
        if (leaderEmail) {
          const permissions = [];
          if (leaderCanCreateProject) permissions.push('can_create_project');
          if (leaderCanInviteMembers) permissions.push('can_invite_team_member');
          if (leaderCanManagePermissions) permissions.push('can_manage_team_permissions');

          await addTeamMember({
            team: team.id,
            email: leaderEmail,
            leader: true,
            permissions,
          });
        }
        success('error:valid.success');
        // Redirect to appropriate page based on mode
        let teamUrl;
        if (institutionalTeamsMode) {
          teamUrl = `/dashboard/institutional-team/${team.id}`;
        } else if (admin) {
          teamUrl = `/admin/team/${team.id}`;
        } else {
          teamUrl = `/dashboard/team/${team.id}`;
        }
        history.push(teamUrl);
        if (modal.current) {
          modal.current.hide();
          reset();
        }
      } catch (error) {
        handleCatched(props, error);
      } finally {
        release();
      }
    }
  };

  const onCreateTeam = async () => {
    const nameOk = nameRef.current.validate(name);
    const leaderEmailOk = canBeTeamLeader || (emailRef && emailRef.current.validate(leaderEmail));
    const ownerOk = !admin || (ownerRef && ownerRef.current.validate(newOwnerId, GUI_EFFECT_FULL));

    // In institutional teams mode with multiple orgs, require selection
    const orgSelectionOk = !institutionalTeamsMode
      || organizations.length <= 1
      || selectedOrganization !== null;

    if (!orgSelectionOk) {
      showError(t('user:team.creation.select-organization-required'));
      return;
    }

    if (nameOk && leaderEmailOk && ownerOk) {
      await createTeam();
    }
  };

  const onLeaderEmailChange = (event) => {
    const value = event.target ? event.target.value : '';
    setLeaderEmail(value);
    if (emailRef) {
      emailRef.current.validate(value);
    }
  };
  const onLeaderEmailPaste = (event) => {
    formatPasteEmailList(event);
    onLeaderEmailChange(event);
  };

  /**
   * Check if user can create teams (not in read-only mode for research teams)
   * Used as triggerCondition for the modal
   */
  const checkCanCreateTeam = () => {
    // eslint-disable-next-line camelcase
    const isResearchTeamsReadOnly = user.read_only_status?.research_teams_read_only || false;

    if (isResearchTeamsReadOnly) {
      showError(t('user:admin-team.read-only-research-teams'));
      return false;
    }
    return true;
  };

  return (
    <LicenseChecker
      limName="can_create_team"
      limitations={user.limitations}
      licMsgModalChildren={children}
      bypassChecking={institutionalTeamsMode}
    >
      <NewModal
        title={t('user:team.add')}
        size="sm"
        trigger={children}
        triggerCondition={checkCanCreateTeam}
        onClosed={reset}
        footer={(
          <div>
            <Button
              id="team-validate"
              color="newturquoise-1"
              className="px-4 contains-loader font-weight-semibold"
              onClick={onCreateTeam}
            >
              {t('common:button.create')}
            </Button>
          </div>
        )}
        ref={modal}
      >
        <form
          className="team-create-modal"
          noValidate
          onSubmit={(e) => e.preventDefault()}
        >
          <LabeledInput
            type="text"
            label={t('user:team.creation.enter-team-name')}
            className="mb-3"
            name="team-name"
            placeholder={t('user:team.creation.name-placeholder')}
            required
            onChange={(e) => setName(e.target.value)}
            value={name}
            ref={nameRef}
          />
          {/* Organization selector for institutional teams mode */}
          {institutionalTeamsMode && organizations.length >= 1 && (
            <div className="mb-3">
              <label className="form-label mb-2">
                {t('user:team.creation.select-organization')}
                <span className="text-danger ml-1">*</span>
              </label>
              {loadingOrganizations ? (
                <MiniLoader />
              ) : (
                <select
                  className="form-control"
                  value={selectedOrganization || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedOrganization(val ? Number(val) : null);
                  }}
                >
                  {organizations.length > 1 && (
                    <option value="">
                      {t('user:team.creation.select-organization-placeholder')}
                    </option>
                  )}
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
          {!canBeTeamLeader && (
            <>
              <LabeledInput
                type="text"
                label={t('user:team.invite-team-leader')}
                name="leader-email"
                placeholder={t('error:placeholder.email')}
                validation="email"
                required
                value={leaderEmail}
                onChange={onLeaderEmailChange}
                onPaste={onLeaderEmailPaste}
                ref={emailRef}
                autoComplete="off"
                className="mb-0"
              />
              <div className="mt-3 mb-3">
                <label className="form-label mb-2">
                  {t('user:team.creation.permissions.leader-title')}
                </label>
                <NewBadge className={`teamcreate-badge-${i18n.language}`} />

                <div className="mb-2">
                  <div className="custom-control custom-checkbox mb-2">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="leader-can-create-project"
                      checked={leaderCanCreateProject}
                      onChange={(e) => setLeaderCanCreateProject(e.target.checked)}
                    />
                    <label className="custom-control-label" htmlFor="leader-can-create-project">
                      {t('user:team.permissions.can_create_project')}
                    </label>
                  </div>

                  <div className="custom-control custom-checkbox mb-2">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="leader-can-invite-members"
                      checked={leaderCanInviteMembers}
                      onChange={(e) => setLeaderCanInviteMembers(e.target.checked)}
                    />
                    <label className="custom-control-label d-flex align-items-center" htmlFor="leader-can-invite-members">
                      <span>{t('user:team.permissions.can_invite_team_member')}</span>
                      <Help iconClassName="ml-2">
                        {t('user:team.creation.permissions.help')}
                      </Help>
                    </label>
                  </div>

                  <div className="custom-control custom-checkbox mb-1">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="leader-can-manage-permissions"
                      checked={leaderCanManagePermissions}
                      onChange={(e) => setLeaderCanManagePermissions(e.target.checked)}
                    />
                    <label className="custom-control-label" htmlFor="leader-can-manage-permissions">
                      {t('user:team.permissions.can_manage_team_permissions')}
                    </label>
                  </div>
                </div>
              </div>
            </>
          )}
          {admin && (
            <>
              <Validator
                required
                ref={ownerRef}
              >
                <UserSelect
                  admin
                  value={newOwnerId}
                  onChange={(e) => setNewOwnerId(e.value)}
                  placeholder={t('project:new-invitation.select-team')}
                />
              </Validator>
              {/* <div className="help-text">
                {t('user:team.invite-team-leader-help')}
              </div> */}
            </>
          )}
        </form>
      </NewModal>
    </LicenseChecker>
  );
}

TeamCreateModal.propTypes = {
  i18n: PropTypes.shape().isRequired,
  children: childrenPropTypes().isRequired,
  admin: PropTypes.bool,
  institutionalTeamsMode: PropTypes.bool,
  organizationIds: PropTypes.arrayOf(PropTypes.number),
};

TeamCreateModal.defaultProps = {
  admin: false,
  institutionalTeamsMode: false,
  organizationIds: [],
};

export default TeamCreateModal;
